'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export const DEFAULT_ACTION_COOLDOWN_MS = 5000

/**
 * Client-side cooldown after an action: each `beginCooldown` clears any pending timer and starts a new full `cooldownMs` window. Clears the timer on unmount.
 *
 * @param cooldownMs - Length of the cooldown window in milliseconds (default 5000).
 */
export const useActionCooldown = (cooldownMs: number = DEFAULT_ACTION_COOLDOWN_MS) => {
    const cooldownTimeoutRef = useRef<number | null>(null)
    const [isCooldownActive, setIsCooldownActive] = useState(false)

    useEffect(
        () => () => {
            if (cooldownTimeoutRef.current !== null) {
                window.clearTimeout(cooldownTimeoutRef.current)
            }
        },
        [],
    )

    const beginCooldown = useCallback((): void => {
        if (cooldownTimeoutRef.current !== null) {
            window.clearTimeout(cooldownTimeoutRef.current)
        }
        setIsCooldownActive(true)
        cooldownTimeoutRef.current = window.setTimeout(() => {
            setIsCooldownActive(false)
            cooldownTimeoutRef.current = null
        }, cooldownMs)
    }, [cooldownMs])

    return { beginCooldown, isCooldownActive }
}
